import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Row, Col } from "stories/layout";
import { Input } from "stories/forms";
import "./derivativeAlignedRow.css";
import { Typography } from "stories/components";
import { useAppContext } from "contexts/AppContext";
import { getOptionStateLabel, shouldShowOptionStateDate } from "components/Forms/OptionStateInput/optionStateLabels";
import { formatDerivativePrice, DERIVATIVE_PRICE_DECIMALS } from "services/helpers";
import TerminationSettlementFields from "views/app/OptionEdit/TerminationSettlementFields";

const ReadOnlyField = ({ name, label, value }) => (
  <Input
    name={name}
    type="text"
    label={label}
    value={value ?? ""}
    disabled
    readOnly
  />
);

const DerivativeTerminationContractCard = ({
  title,
  contract,
  stockMarket,
  editable = false,
  settlementEditable = false,
  settlement,
  onFieldChange,
  showStateDate = false,
  stateDate,
  afterTitle = null,
}) => {
  const { t } = useTranslation();
  const { enums, getDerivativeType, getOptionsAssetType } = useAppContext();

  const derivativeLabel = getDerivativeType(contract?.derivative_type)?.value ?? contract?.derivative_type ?? "";
  const assetTypeLabel = getOptionsAssetType(contract?.asset_type)?.value ?? contract?.asset_type ?? "";
  const roleLabel =
    enums?.options?.option?.role?.find((item) => String(item.key) === String(contract?.role))?.value ??
    contract?.role ??
    "";
  const stateLabel =
    getOptionStateLabel(t, contract?.option_state) ||
    enums?.options?.option?.option_state?.find((item) => String(item.key) === String(contract?.option_state))?.value ||
    contract?.option_state ||
    "";
  const rightLabel =
    contract?.option_right === 2
      ? t("Call option", "Call option")
      : contract?.option_right === 3
      ? t("Put option", "Put option")
      : "";

  const formatDate = (date) => (date ? moment(date).format("D.M.YYYY") : "");

  return (
    <div className="derivative-termination-contract-card border rounded p-3 mb-3 bg-white">
      <Typography variant="h4" className={afterTitle ? "mb-2" : "mb-3"}>
        {title}
      </Typography>
      {afterTitle ? <div className="mb-3">{afterTitle}</div> : null}

      <Row className="derivative-aligned-row">
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_derivative_type`}
            label={t("option_edit_derivative_type", "Johdannaistyyppi")}
            value={derivativeLabel}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_asset_type`}
            label={t("option_edit_asset_type", "Kohde-etuus tyyppi")}
            value={assetTypeLabel}
          />
        </Col>
      </Row>

      <Row className="derivative-aligned-row">
        {stockMarket && (
          <Col xs="12" md="6" className="mb-3">
            <ReadOnlyField
              name={`${title}_stock_market`}
              label={t("derivative_termination_stock_market", "Pörssi")}
              value={stockMarket}
            />
          </Col>
        )}
        <Col xs="12" md={stockMarket ? 6 : 12} className="mb-3">
          <ReadOnlyField
            name={`${title}_asset_name`}
            label={t("option_edit_asset_stock", "Osake")}
            value={contract?.asset_name}
          />
        </Col>
      </Row>

      <Row className="derivative-aligned-row">
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_lot_size`}
            label={t("option_edit_lot_size", "Erän koko")}
            value={contract?.lot_size}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_lot_amount`}
            label={t("option_edit_lot_amount", "Erien määrä")}
            value={contract?.lot_amount}
          />
        </Col>
      </Row>

      <Row className="derivative-aligned-row">
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_start_date`}
            label={t("option_edit_start_date", "Aloituspäivä")}
            value={formatDate(contract?.start_date)}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_end_date`}
            label={t("option_edit_end_date", "Eräpäivä")}
            value={formatDate(contract?.end_date)}
          />
        </Col>
      </Row>

      <Row>
        <Col xs="12" md="6" className="mb-3">
          {editable ? (
            <Input
              name={`${title}_trade_id`}
              type="text"
              label={t("derivative_termination_new_trade_id", "Tunnus")}
              value={contract?.trade_id ?? ""}
              onChange={({ target }) => onFieldChange?.("trade_id", target.value)}
            />
          ) : (
            <ReadOnlyField
              name={`${title}_trade_id`}
              label={t("option_edit_trade_id", "Tunniste")}
              value={contract?.trade_id}
            />
          )}
        </Col>
      </Row>

      <Row className="derivative-aligned-row">
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_owner`}
            label={t("option_edit_owner", "Ostaja")}
            value={contract?.owner_name}
          />
        </Col>
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_writer`}
            label={t("option_edit_writer", "Myyjä")}
            value={contract?.writer_name}
          />
        </Col>
      </Row>

      <Row className="derivative-aligned-row">
        <Col xs="12" md="6" className="mb-3">
          <ReadOnlyField
            name={`${title}_role`}
            label={t("option_edit_role", "Rooli")}
            value={roleLabel}
          />
        </Col>
        {contract?.derivative_type == 2 && (
          <Col xs="12" md="6" className="mb-3">
            <ReadOnlyField
              name={`${title}_right`}
              label={t("option_edit_right", "Tyyppi")}
              value={rightLabel}
            />
          </Col>
        )}
      </Row>

      <Row className="derivative-aligned-row">
        <Col xs="12" md="4" className="mb-3">
          <ReadOnlyField
            name={`${title}_quantity`}
            label={t("option_edit_quantity", "Määrä")}
            value={contract?.quantity}
          />
        </Col>
        <Col xs="12" md="4" className="mb-3">
          <ReadOnlyField
            name={`${title}_premium_total`}
            label={t("option_edit_premium_total", "Preemio yhteensä")}
            value={`${formatDerivativePrice(contract?.premium_total ?? 0)} €`}
          />
        </Col>
        <Col xs="12" md="4" className="mb-3">
          <ReadOnlyField
            name={`${title}_state`}
            label={t("option_edit_state", "Tila")}
            value={stateLabel}
          />
        </Col>
        {shouldShowOptionStateDate(contract?.option_state, showStateDate) && (
          <Col xs="12" md="4" className="mb-3">
            <ReadOnlyField
              name={`${title}_option_state_date`}
              label={t("option_edit_state_date", "Tilan päivämäärä")}
              value={formatDate(stateDate ?? contract?.option_state_date)}
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col xs="12" md={editable ? 6 : 12} className="mb-3">
          {editable ? (
            <Input
              name={`${title}_other_expenses`}
              type="decimal"
              decimalPlaces={DERIVATIVE_PRICE_DECIMALS}
              label={t("option_edit_other_expenses", "Muut kulut")}
              value={contract?.other_expenses ?? ""}
              onChange={({ target }) => onFieldChange?.("other_expenses", target.value)}
            />
          ) : (
            <ReadOnlyField
              name={`${title}_other_expenses`}
              label={t("option_edit_other_expenses", "Muut kulut")}
              value={`${formatDerivativePrice(contract?.other_expenses ?? 0)} €`}
            />
          )}
        </Col>
      </Row>

      {settlement ? (
        <TerminationSettlementFields
          quantity={settlement.quantity}
          quantityReadOnly={settlement.quantityReadOnly}
          onQuantityChange={settlement.onQuantityChange}
          unitPrice={settlement.unitPrice}
          onUnitPriceChange={settlement.onUnitPriceChange}
          total={settlement.total}
          onTotalChange={settlement.onTotalChange}
          strikePrice={settlement.strikePrice ?? contract?.strike_price}
          role={settlement.role ?? contract?.role}
          optionRight={settlement.optionRight ?? contract?.option_right}
          derivativeType={settlement.derivativeType ?? contract?.derivative_type}
          unitClassName={settlement.unitClassName}
        />
      ) : (Number(contract?.option_state) === 5 || settlementEditable) ? (
        <TerminationSettlementFields
          quantity={contract?.quantity}
          unitPrice={contract?.termination_price}
          onUnitPriceChange={settlementEditable ? (value) => onFieldChange?.("termination_price", value) : undefined}
          total={contract?.receives}
          onTotalChange={settlementEditable ? (value) => onFieldChange?.("receives", value) : undefined}
          strikePrice={contract?.strike_price}
          role={contract?.role}
          optionRight={contract?.option_right}
          derivativeType={contract?.derivative_type}
        />
      ) : null}

      {editable && (
        <Row>
          <Col xs="12" className="mb-0">
            <Input
              name={`${title}_notes`}
              type="textarea"
              label={t("option_edit_notes", "Muistiinpanot")}
              value={contract?.notes ?? ""}
              onChange={({ target }) => onFieldChange?.("notes", target.value)}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default DerivativeTerminationContractCard;
